import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

const AdminProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');
  const [selectedLang, setSelectedLang] = useState(i18n.language || 'fr');
  const [langSaving, setLangSaving] = useState(false);
  const [langMsg, setLangMsg] = useState('');

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const { data: { user: authUser } } = await supabase?.auth?.getUser();
        if (!authUser) { navigate('/login-authentication'); return; }
        setUser(authUser);
        const { data: profileData } = await supabase?.from('users')?.select('*')?.eq('id', authUser?.id)?.single();
        if (profileData) {
          setProfile(profileData);
          setName(profileData?.name || '');
          setPhone(profileData?.phone || '');
          setBio(profileData?.bio || '');
        }
      } catch (err) {
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const getInitials = (n) => {
    if (!n) return '?';
    const parts = n?.trim()?.split(' ');
    if (parts?.length >= 2) return (parts?.[0]?.charAt(0) + parts?.[1]?.charAt(0))?.toUpperCase();
    return n?.trim()?.charAt(0)?.toUpperCase();
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const { error } = await supabase?.from('users')?.update({ name, phone, bio })?.eq('id', user?.id);
      if (error) throw error;
      setSaveMsg(t('admin.adminProfile.saved', 'Enregistré'));
    } catch (err) {
      setSaveMsg(t('admin.adminProfile.error', 'Erreur'));
    } finally {
      setSaving(false);
    }
  };

  const handleLangSave = async () => {
    if (!user) return;
    setLangSaving(true);
    setLangMsg('');
    try {
      const { error } = await supabase?.from('users')?.update({ lang: selectedLang })?.eq('id', user?.id);
      if (error) { setLangMsg('❌ ' + error?.message); return; }
      await i18n?.changeLanguage(selectedLang);
      setLangMsg('✅');
    } catch (err) {
      setLangMsg('❌ ' + err?.message);
    } finally {
      setLangSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordMsg('');
    if (!newPassword || newPassword?.length < 8) {
      setPasswordMsg(t('admin.adminProfile.minChars', 'Min. 8 caractères'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg(t('admin.adminProfile.passwordsDontMatch', 'Mots de passe différents'));
      return;
    }
    setPasswordSaving(true);
    try {
      const { error } = await supabase?.auth?.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordMsg(t('admin.adminProfile.passwordUpdated', 'Mot de passe mis à jour'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMsg(t('admin.adminProfile.error'));
    } finally {
      setPasswordSaving(false);
    }
  };


  const inputStyle = {
    backgroundColor: '#111111', border: '1px solid #1f1f1f', color: '#ffffff',
    fontFamily: 'Inter, sans-serif', fontSize: '16px', borderRadius: '8px',
    padding: '10px 14px', width: '100%', outline: 'none', transition: 'border-color 0.15s',
  };
  const readonlyInputStyle = { ...inputStyle, color: 'rgba(255,255,255,0.35)', cursor: 'not-allowed' };
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.45)', fontFamily: 'Sora, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' };
  const sectionStyle = { backgroundColor: '#111111', border: '1px solid #1f1f1f', borderRadius: '12px', padding: '20px', marginBottom: '16px' };
  const sectionTitleStyle = { fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #1f1f1f' };
  const btnStyle = { backgroundColor: 'var(--color-primary)', border: 'none', color: 'white', fontFamily: 'Sora, sans-serif', cursor: 'pointer', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 700 };

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} userEmail={profile?.email} />
      </div>
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.adminProfile.title', 'Profil Admin')} />
        <div className="sticky top-0 z-10 px-6 py-4 border-b flex items-center" style={{ backgroundColor: '#0a0a0a', borderColor: '#1f1f1f' }}>
          <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{t('admin.adminProfile.title', 'Profil Admin')}</h1>
        </div>
        <div className="px-6 py-4 max-w-2xl">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)]?.map((_, i) => (
                <div key={i} className="h-32 rounded-xl animate-pulse" style={{ backgroundColor: '#111111' }} />
              ))}
            </div>
          ) : (
            <>
              {/* Identité */}
              <div style={sectionStyle}>
                <p style={sectionTitleStyle}>{t('admin.adminProfile.identity', 'Identité')}</p>
                <div className="flex items-center gap-5">
                  <div className="rounded-full flex items-center justify-center font-bold text-white flex-shrink-0" style={{ width: 52, height: 52, backgroundColor: 'var(--color-primary)', fontFamily: 'Sora, sans-serif', fontSize: '18px' }}>
                    {getInitials(name || profile?.name)}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{name || profile?.name || 'Admin'}</p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>{profile?.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: 'rgba(229,32,42,0.12)', color: '#E5202A', border: '1px solid rgba(229,32,42,0.25)' }}>Admin</span>
                  </div>
                </div>
              </div>

              {/* Informations personnelles */}
              <div style={sectionStyle}>
                <p style={sectionTitleStyle}>{t('admin.adminProfile.personalInfo', 'Informations personnelles')}</p>
                <div className="space-y-4">
                  <div>
                    <label style={labelStyle}>{t('admin.adminProfile.name', 'Nom')}</label>
                    <input type="text" value={name} onChange={(e) => setName(e?.target?.value)} style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('admin.adminProfile.email', 'Email')}</label>
                    <input type="email" value={profile?.email || ''} readOnly style={readonlyInputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('admin.adminProfile.phone', 'Téléphone')}</label>
                    <input type="tel" value={phone} onChange={(e) => setPhone(e?.target?.value)} placeholder="+216 XX XXX XXX" style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <button onClick={handleSave} disabled={saving} style={{ ...btnStyle, opacity: saving ? 0.7 : 1 }}>
                    {saving ? '...' : t('admin.adminProfile.save', 'Enregistrer')}
                  </button>
                  {saveMsg && <span className="text-sm" style={{ color: saveMsg === t('admin.adminProfile.saved', 'Enregistré') ? '#22c55e' : '#ef4444', fontFamily: 'Inter, sans-serif' }}>{saveMsg}</span>}
                </div>
              </div>

              {/* Langue */}
              <div style={sectionStyle}>
                <p style={sectionTitleStyle}>{t('admin.adminProfile.language', 'Langue')}</p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setSelectedLang(lang.code)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '12px 14px', borderRadius: '10px',
                        border: selectedLang === lang.code ? '1px solid #E5202A' : '1px solid #2a2a2a',
                        backgroundColor: selectedLang === lang.code ? 'rgba(229,32,42,0.08)' : '#1a1a1a',
                        cursor: 'pointer', color: 'white',
                        fontFamily: 'Inter, sans-serif', fontSize: '14px',
                        fontWeight: selectedLang === lang.code ? 600 : 400,
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleLangSave}
                    disabled={langSaving}
                    className="px-5 py-2.5 rounded-lg text-sm font-bold text-white"
                    style={{ backgroundColor: 'var(--color-primary)', border: 'none', cursor: langSaving ? 'not-allowed' : 'pointer', opacity: langSaving ? 0.7 : 1, fontFamily: 'Sora, sans-serif' }}
                  >
                    {langSaving ? '...' : t('admin.adminProfile.saveLanguage', 'Enregistrer la langue')}
                  </button>
                  {langMsg && <span style={{ fontSize: '16px' }}>{langMsg}</span>}
                </div>
              </div>

              {/* Sécurité */}
              <div style={sectionStyle}>
                <p style={sectionTitleStyle}>{t('admin.adminProfile.security', 'Sécurité — Mon mot de passe')}</p>
                <div className="space-y-4">
                  <div>
                    <label style={labelStyle}>{t('admin.adminProfile.currentPassword', 'Mot de passe actuel')}</label>
                    <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e?.target?.value)} placeholder="••••••••" style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('admin.adminProfile.newPassword', 'Nouveau mot de passe')}</label>
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e?.target?.value)} placeholder={t('admin.adminProfile.minChars', 'Min. 8 caractères')} style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('admin.adminProfile.confirmLabel', 'Confirmer')}</label>
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e?.target?.value)} placeholder="••••••••" style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <button onClick={handlePasswordChange} disabled={passwordSaving} style={{ ...btnStyle, backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', opacity: passwordSaving ? 0.7 : 1 }}>
                    {passwordSaving ? '...' : t('admin.adminProfile.updatePassword', 'Mettre à jour')}
                  </button>
                  {passwordMsg && <span className="text-sm" style={{ color: passwordMsg === t('admin.adminProfile.passwordUpdated', 'Mot de passe mis à jour') ? '#22c55e' : '#ef4444', fontFamily: 'Inter, sans-serif' }}>{passwordMsg}</span>}
                </div>
              </div>

              {/* Déconnexion */}
              <div style={sectionStyle}>
                <p style={sectionTitleStyle}>{t('admin.adminProfile.session', 'Session')}</p>
                <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.adminProfile.logoutDescription', 'Se déconnecter de NoiRock sur cet appareil.')}
                </p>
                <button
                  onClick={async () => { await supabase?.auth?.signOut(); navigate('/login-authentication'); }}
                  className="flex items-center gap-2"
                  style={{ ...btnStyle, backgroundColor: 'transparent', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  {t('admin.adminProfile.logout', 'Déconnexion')}
                </button>
              </div>

            </>
          )}
        </div>
      </main>
      <AdminBottomNav />
    </div>
  );
};

export default AdminProfile;
